import { useUserContext } from '@atlassian/navigation-bff-data-provider/context';
import { useFetchUser as useBFFetchUser } from '@atlassian/navigation-bff-data-provider/use-fetch-user';

import { REQUIRED_USER_ITEMS } from '../../../../constants';

interface FetchUserIfNeededParams {
	items?: Array<{ ati?: string }>;
	sections?: Array<{ items?: Array<{ ati?: string }> }>;
}

/**
 * Hook that provides a function to conditionally fetch user data based on menu items.
 * Only fetches if:
 * 1. Items contain ATIs that require user data (defined in REQUIRED_USER_ITEMS)
 * 2. User data is not already available
 *
 * @returns Object containing fetchUserIfNeeded function
 */
export function useFetchUser(): {
	fetchUserIfNeeded: ({ items, sections }: FetchUserIfNeededParams) => void;
} {
	const { user: currentUser } = useUserContext();
	const { fetchUser } = useBFFetchUser();

	/**
	 * Conditionally fetches user data if menu items require it.
	 * Supports both V1 (flat items array) and V2 (sections with items) data structures.
	 *
	 * @param params - Object containing either items array or sections array
	 */
	const fetchUserIfNeeded = ({ items, sections }: FetchUserIfNeededParams): void => {
		// Skip if user already available
		if (currentUser) {
			return;
		}

		// Check V1 format (flat items array)
		if (items) {
			const willNeedUser = items.some((item) => {
				return item.ati && REQUIRED_USER_ITEMS.includes(item.ati);
			});

			if (willNeedUser) {
				void fetchUser();
			}
		}

		// Check V2 format (sections with items)
		if (sections && Array.isArray(sections)) {
			const willNeedUser = sections.some(
				(section) =>
					Array.isArray(section.items) &&
					section.items.some((item) => item && item.ati && REQUIRED_USER_ITEMS.includes(item.ati)),
			);

			if (willNeedUser) {
				void fetchUser();
			}
		}
	};

	return {
		fetchUserIfNeeded,
	};
}
